/**
 * FocusNavigationDialog is a reusable base for dialogs with gamepad/keyboard support.
 *
 * Why: All dialogs need consistent controller input handling (B/Escape to close),
 * focus area management (so header/content don't receive events while dialog is open),
 * and list navigation for buttons. This extracts that complexity into a reusable base.
 *
 * When to use: Use as the base for any dialog that needs focus navigation support.
 * Provides DialogNavigationContext for child components to access navigation state.
 * See CloseConfirmDialog and InfoDialog for usage examples.
 */
import * as React from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { cn } from "@merels/ui"
import { useControllerInputDep, useFocusNavigationDep } from "../../providers/FocusNavigationProvider/FocusNavigationProvider"
import { useListNavigation } from "../../../hooks/useListNavigation"
import { useSignals } from '@preact/signals-react/runtime'

export interface DialogNavigationItem {
  /** Unique identifier for the navigation item */
  id: string
  /** Optional data associated with this item */
  data?: unknown
}

export interface FocusNavigationDialogProps<T extends DialogNavigationItem = DialogNavigationItem> {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** Trigger element that opens the dialog */
  trigger: React.ReactElement
  /** Dialog title */
  title?: React.ReactNode
  /** Dialog content */
  children: React.ReactNode | ((navigation: DialogNavigationContext) => React.ReactNode)
  /** Optional test ID for the dialog content */
  testId?: string
  /** Optional className for the dialog content */
  contentClassName?: string
  /** Whether to handle cancel/exit events to close the dialog (default: true) */
  handleCancelExit?: boolean
  /** Optional navigation items for list navigation */
  navigationItems?: T[]
  /** Callback when a navigation item is selected */
  onNavigationSelect?: (item: DialogNavigationItem, index: number) => void
}

export interface DialogNavigationContext {
  /** Currently focused navigation item index */
  focusedIndex: number
  /** Register a navigation item ref */
  registerItem: (index: number, element: HTMLElement | null) => void
}

const DialogNavigationContext = React.createContext<DialogNavigationContext | null>(null)

export function useDialogNavigation(): DialogNavigationContext {
  const context = React.useContext(DialogNavigationContext)
  if (!context) {
    throw new Error('useDialogNavigation must be used within FocusNavigationDialog with navigationItems')
  }
  return context
}

export const FocusNavigationDialog = React.forwardRef<HTMLButtonElement, FocusNavigationDialogProps>(
  ({
    open,
    onOpenChange,
    trigger,
    title,
    children,
    testId,
    contentClassName,
    handleCancelExit = true,
    navigationItems,
    onNavigationSelect,
  }, ref) => {
    useSignals()
    const controllerInput = useControllerInputDep()
    const focusNavigation = useFocusNavigationDep()
    const isFocusNavActive = focusNavigation.isActive.value

    // Set up list navigation if items are provided
    // skipFocusCheck=true because dialogs should always handle events when open
    const dialogNav = useListNavigation(
      navigationItems || [],
      React.useCallback((item, index) => {
        onNavigationSelect?.(item as DialogNavigationItem, index)
      }, [onNavigationSelect]),
      open && isFocusNavActive && !!navigationItems, // Active when dialog is open, focus nav is active, and items are provided
      undefined, // onNavigate callback
      true // skipFocusCheck - dialogs should always handle events when active
    )

    // Handle cancel/exit to close the dialog
    React.useEffect(() => {
      if (!handleCancelExit || !open || !isFocusNavActive) return

      const unsubscribe = controllerInput.subscribe((event) => {
        if (event.type === 'cancel' || event.type === 'exit') {
          // B button or Escape closes the dialog
          onOpenChange(false)
        }
      })

      return unsubscribe
    }, [controllerInput, open, isFocusNavActive, onOpenChange, handleCancelExit])

    // When dialog opens, set focus area to 'dialog' so neither header nor content
    // handle navigate/select events. Otherwise both could receive Enter and trigger
    // wrong actions (e.g. content placing a piece, or header opening info dialog).
    React.useEffect(() => {
      if (!isFocusNavActive) return
      if (open) {
        focusNavigation.setFocusArea('dialog')
      }
    }, [open, isFocusNavActive, focusNavigation])

    // Return focus to content area when dialog is dismissed
    const prevOpenRef = React.useRef(open)
    React.useEffect(() => {
      // When dialog closes (was open, now closed), return focus to content
      if (prevOpenRef.current && !open && isFocusNavActive) {
        focusNavigation.setFocusArea('content')
      }
      prevOpenRef.current = open
    }, [open, isFocusNavActive, focusNavigation])

    // Note: Select events are handled by useListNavigation internally
    // It will call onNavigationSelect when select event occurs

    // Navigation context value
    const navigationContext: DialogNavigationContext = React.useMemo(() => ({
      focusedIndex: dialogNav.focusedIndex,
      registerItem: dialogNav.registerItem,
    }), [dialogNav.focusedIndex, dialogNav.registerItem])

    // Render children - if it's a function, pass navigation context
    const content = typeof children === 'function'
      ? children(navigationContext)
      : children

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Trigger asChild ref={ref}>
          {trigger}
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-overlay z-[10000] transition-opacity duration-300 data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
          <Dialog.Content
            className={cn(
              "fixed left-1/2 top-1/2 z-[10000] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-6 shadow-lg",
              "transition-all duration-300",
              "data-[state=open]:opacity-100 data-[state=closed]:opacity-0",
              "data-[state=open]:scale-100 data-[state=closed]:scale-95",
              contentClassName
            )}
            data-testid={testId}
          >
            {title && (
              <Dialog.Title className="text-lg font-bold text-primary mb-2">
                {title}
              </Dialog.Title>
            )}
            {navigationItems ? (
              <DialogNavigationContext.Provider value={navigationContext}>
                {content}
              </DialogNavigationContext.Provider>
            ) : (
              content
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }
)
FocusNavigationDialog.displayName = "FocusNavigationDialog"
